import type { UserResource } from '@clerk/shared/types'
import consola from 'consola'
import { firstValueFrom } from 'rxjs'
import type { RouteLocationNormalized } from 'vue-router'
import { useAppMachine } from '~/composables/useAppMachine'
import { type StcsCompRoleEnum, StcsEntityStatusEnum, type StcsOrgRoleEnum } from '~/protos/gen/stcs_enums.entity.ts'
import { getNextOnboardingStepUrl } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store'
import { urlCtxPartObj } from '~/types/route.entity'
import { defineNuxtRouteMiddleware, navigateTo, useNuxtApp, useRuntimeConfig } from '#imports'

const logger = consola.withTag('[Auth Middleware]')

export interface PagePermissions {
  // Defaults to 'AND'.
  logic?: 'AND' | 'OR'
  // User must have one of these roles at the Organization level.
  orgRoles?: StcsOrgRoleEnum[]
  // User must have one of these roles for the specific Company in the URL (not in the store!).
  compRoles?: StcsCompRoleEnum[]
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Auth routing logic
export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  // =========================================================================
  // INITIAL AUTH GATES
  // =========================================================================
  const { $clerk } = useNuxtApp()
  const userIsAuthenticated = $clerk.session?.status === 'active' && !!$clerk.user?.id
  const isMatched = to.matched.length > 0
  const isAuthPage = to.path === urlCtxPartObj.ENTER_EMAIL.path || to.path === urlCtxPartObj.OTP.path
  const isPublicPage = to.meta.requiresAuth === false

  logger.info(`Navigating to: ${to.path}`)

  if (userIsAuthenticated) {
    if (!isMatched) {
      logger.warn(`No route matched for authenticated user at path: ${to.fullPath}. Redirecting to /org`)
      return navigateTo('/org')
    }
    if (isAuthPage) {
      return navigateTo(urlCtxPartObj.ORG.path)
    }
  } else if (!(isAuthPage || isPublicPage)) {
    return navigateTo(urlCtxPartObj.ENTER_EMAIL.path)
  }

  // =========================================================================
  // STATE INIT
  // =========================================================================
  if (userIsAuthenticated && $clerk.user !== null) {
    const ctxStore = useCtxStore()
    if (!ctxStore.ref_user.id && $clerk.user) {
      logger.debug('Initializing user and org list...')
      await firstValueFrom(useCtxStore().setIdpUser($clerk.user as UserResource))
      await firstValueFrom(useCtxStore().setRefOrgList())
    }

    if (to.path === urlCtxPartObj.ROOT.path) {
      return navigateTo(urlCtxPartObj.ORG.path)
    }

    // =========================================================================
    // PAGE-LEVEL ACCESS CONTROL
    // =========================================================================
    const permissions = to.meta.permissions as PagePermissions | undefined

    if (permissions) {
      const { orgId, compId } = to.params
      let hasOrgPermission = true
      let hasCompPermission = true

      const currentUserId = ctxStore.ref_user.id?.extractId() // Use optional chaining for safety

      // A. Check Organization roles
      if (permissions.orgRoles && permissions.orgRoles.length > 0) {
        if (typeof orgId === 'string' && currentUserId) {
          const userOrgRole = ctxStore.getOrgRoleForUser(orgId, currentUserId) // Pass the string ID
          hasOrgPermission = userOrgRole ? permissions.orgRoles.includes(userOrgRole) : false
        } else {
          hasOrgPermission = false
        }
      }

      // B. Check Company roles
      if (permissions.compRoles && permissions.compRoles.length > 0) {
        if (typeof orgId === 'string' && typeof compId === 'string' && currentUserId) {
          const userCompRole = ctxStore.getCompRoleForUser(orgId, compId, currentUserId) // Pass the string ID
          hasCompPermission = userCompRole ? permissions.compRoles.includes(userCompRole) : false
        } else {
          hasCompPermission = false
        }
      }

      // C. Combine results based on logic
      const logic = permissions.logic || 'AND'
      const isAllowed = logic === 'AND' ? hasOrgPermission && hasCompPermission : hasOrgPermission || hasCompPermission

      // D. Enforce the decision
      if (!isAllowed) {
        logger.warn(`Access Denied to ${to.path}. Org Role OK: ${hasOrgPermission}, Comp Role OK: ${hasCompPermission}. Logic: ${logic}.`)
        return navigateTo('/main/no-access')
      }
      logger.debug(`Access Granted to ${to.path}. Org Role OK: ${hasOrgPermission}, Comp Role OK: ${hasCompPermission}.`)
    }

    // =========================================================================
    // BUSINESS LOGIC (some old stuff here)
    // =========================================================================
    const { locale, locales, setLanguage } = useAppMachine()
    const runtimeConfig = useRuntimeConfig()
    const { orgId, compId } = to.params

    ctxStore.loadActiveIds(orgId as string | undefined, compId as string | undefined)

    // 1. Organization Check
    if (ctxStore.active_org_id && !ctxStore.ref_org_map[ctxStore.active_org_id]) {
      logger.warn(`Active Org ID ${ctxStore.active_org_id} not found in user orgs. Clearing.`)
      ctxStore.active_org_id = ''
    }

    // 2. Company Check
    // If the active comp ID (from settings) isn't in the loaded company map, clear it.
    if (ctxStore.active_comp_id && !ctxStore.ref_comp_map[ctxStore.active_comp_id]) {
      logger.warn(`Active Comp ID ${ctxStore.active_comp_id} not found in user comps. Clearing.`)
      ctxStore.active_comp_id = ''
    }

    // 3. Language (Locale) Check
    // If the loaded locale isn't supported, revert to default.
    if (locale.value && !locales.value.includes(locale.value)) {
      logger.warn(`Invalid locale ${locale.value} detected. Resetting to default.`)
      const defaultLang = runtimeConfig.public.tolgee.defaultLanguage || 'en'
      setLanguage(defaultLang)
    }

    if (ctxStore.active_comp_id && !ctxStore.ref_comp_map[ctxStore.active_comp_id]?.has_data) {
      logger.debug(`Data not present for compId: ${ctxStore.active_comp_id}. Triggering load.`)
      ctxStore.changeCompById(ctxStore.active_comp_id)
    }

    const targetOrgId = orgId && typeof orgId === 'string' ? orgId : ctxStore.active_org_id
    const targetOrgIsSandbox = targetOrgId ? ctxStore.ref_org_map[targetOrgId]?.sandbox : false

    // Prevent stale company ID when switching org
    let targetCompId = compId && typeof compId === 'string' ? compId : ctxStore.active_comp_id
    if (targetOrgId && targetCompId && !compId) {
      const activeOrgForCheck = ctxStore.ref_org_map[targetOrgId]
      // If the org exists but does NOT contain the target company, clear the target company
      if (activeOrgForCheck && !activeOrgForCheck.meta?.ref_comp_map?.[targetCompId]) {
        logger.debug(`Stale Active Comp ID ${targetCompId} detected for Org ${targetOrgId}. Clearing.`)
        targetCompId = ''
        ctxStore.active_comp_id = '' // Clear active comp ID from store
      }
    }

    // Allow access to new org creation
    //
    // THIS IS A DIRTY HACK BECAUSE targetOrgId gets pulled from ctxStore.active_org_id above
    if (to.path === urlCtxPartObj.CREATE_ACCOUNT.path) {
      return
    }

    // Paywall Enforcement for non-sandbox Orgs
    if (targetOrgId && !targetOrgIsSandbox) {
      const org = ctxStore.ref_org_map[targetOrgId]
      if (org) {
        const paywallPath = urlCtxPartObj.SElECT_PAYMENT_PLAN.path.replace(':orgId', targetOrgId)
        const isPaywallPath = to.path === paywallPath
        const isOrgActive = org.status === StcsEntityStatusEnum.SES_ACTIVE || org.status === StcsEntityStatusEnum.SES_APPROVED
        const isSubscriptionActive = org.subscription?.subscription_status === StcsEntityStatusEnum.SES_ACTIVE || org.subscription?.subscription_status === StcsEntityStatusEnum.SES_APPROVED
        const isOrgSelector = to.path === urlCtxPartObj.ORG.path

        if (!((isOrgActive && isSubscriptionActive) || isPaywallPath || isOrgSelector)) {
          logger.warn(`Paywall enforced for Org ${targetOrgId}. Statuses: Org=${org.status}, Sub=${org.subscription?.subscription_status}. Redirecting to ${paywallPath}`)
          return navigateTo(paywallPath)
        }

        if (isOrgActive && isSubscriptionActive && isPaywallPath) {
          const onboarding = org.onboarding
          if (onboarding && !onboarding.is_completed) {
            const nextUrl = getNextOnboardingStepUrl(onboarding, targetOrgId)
            logger.debug(`Subscription active. Redirecting from paywall to onboarding step: ${nextUrl}`)
            return navigateTo(nextUrl)
          }
          const orgDashboardPath = `/org/${targetOrgId}`
          logger.debug(`Subscription active. Redirecting from paywall to org dashboard: ${orgDashboardPath}`)
          return navigateTo(orgDashboardPath)
        }
      }
    }

    // Onboarding Enforcement
    if (targetOrgId && !targetOrgIsSandbox) {
      const org = ctxStore.ref_org_map[targetOrgId]
      const isSubscriptionActive = org.subscription?.subscription_status === StcsEntityStatusEnum.SES_ACTIVE || org.subscription?.subscription_status === StcsEntityStatusEnum.SES_APPROVED
      const isWelcomeStepDone = org?.onboarding?.steps_map?.welcome?.done
      logger.info(`Checking onboarding status for Org ${targetOrgId}`)
      // logger.info(`Onboarding object:  ${org?.onboarding ? JSON.stringify(org.onboarding) : 'undefined'}`)
      if (org?.onboarding && !org.onboarding.is_completed) {
        // Same check as in onboarding middleware to redirect paused onboarding
        if (org?.onboarding.is_paused || (!org?.onboarding.has_started && isSubscriptionActive && isWelcomeStepDone)) {
          const nextUrl = org?.onboarding?.steps_map?.sandbox?.url
          if (nextUrl && to.path !== nextUrl) {
            logger.debug('Onboarding paused. Reroute to sandbox:', nextUrl)
            return navigateTo(nextUrl)
          }
        }

        const isOrgSelector = to.path === urlCtxPartObj.ORG.path
        const isOnboardingRoute = to.meta.isOnboarding === true
        const isPaywallRoute = to.path === urlCtxPartObj.SElECT_PAYMENT_PLAN.path.replace(':orgId', targetOrgId)

        if (!(isOnboardingRoute || isPaywallRoute || isOrgSelector)) {
          const resumeUrl = getNextOnboardingStepUrl(org.onboarding, targetOrgId)
          logger.warn(`User in onboarding tried to access non-onboarding page ${to.path}. Redirecting to ${resumeUrl}`)
          return navigateTo(resumeUrl)
        }
      }
      if (targetOrgIsSandbox) {
        const isAllowedSandboxRoute =
          to.path.startsWith(urlCtxPartObj.DASHBOARD.path.replace(':orgId', targetOrgId).replace(':compId', targetCompId)) ||
          to.path.startsWith(urlCtxPartObj.FINS.path.replace(':orgId', targetOrgId).replace(':compId', targetCompId)) ||
          to.path.startsWith(urlCtxPartObj.ANALYSIS.path.replace(':orgId', targetOrgId).replace(':compId', targetCompId)) ||
          to.path.startsWith(urlCtxPartObj.PLAN.path.replace(':orgId', targetOrgId).replace(':compId', targetCompId)) ||
          to.path === urlCtxPartObj.USER_APP_SETTINGS.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.PROFILE_SETTINGS.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.ORGANIZATION_SETTINGS.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.COMPANIES_SETTINGS.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.USER_MANAGEMENT.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.BILLING.path.replace(':orgId', targetOrgId) ||
          to.path === urlCtxPartObj.ORG.path

        if (!isAllowedSandboxRoute) {
          const targetURL = urlCtxPartObj.ORG.path
          logger.warn(`Allowed routes in Sandbox org ${targetOrgId} and ${targetCompId} for ${to.path} (e.g. ${urlCtxPartObj.DASHBOARD.path.replace(':orgId', targetOrgId).replace(':compId', targetCompId)}):`, isAllowedSandboxRoute)
          logger.warn(`Path ${to.path} is restricted in Sandbox org ${targetOrgId}. Redirecting to org selector.`)
          logger.warn(`Sandbox org access restriction: attempted access to ${to.path}. Redirecting to org selector.`)
          return navigateTo(targetURL)
        }
      }
    }

    // Access control
    if (targetOrgId) {
      const activeOrg = ctxStore.ref_org_map[targetOrgId]

      // Verify organization access
      if (activeOrg) {
        // Verify company access in the context of the organization
        if (targetCompId) {
          const userCompAccessMap = activeOrg.meta?.ref_comp_map
          const hasAccess = !!userCompAccessMap?.[targetCompId]

          // Special case: allow if it's a Sandbox Org
          if (activeOrg.sandbox) {
            if (!hasAccess) {
              logger.warn(`Sandbox Company ${targetCompId} not found in Org ${targetOrgId}.`)
              // Prevent loop
              if (to.name !== 'org-orgId') {
                return navigateTo({ name: 'org-orgId', params: { orgId: targetOrgId } })
              }
            }
          } else if (!hasAccess) {
            // Prevent loop
            logger.warn(`Access denied. CompId ${targetCompId} does not belong to OrgId ${targetOrgId} or user lacks permissions.`)
            if (to.path !== urlCtxPartObj.ORG.path) {
              return navigateTo(urlCtxPartObj.ORG.path)
            }
          }
        }
      } else if (to.path !== urlCtxPartObj.ORG.path && to.path !== urlCtxPartObj.CREATE_ACCOUNT.path) {
        // Organization invalid or not found in user list
        logger.warn(`Access denied. User does not belong to orgId: ${targetOrgId}. Redirecting to Org Selector.`)
        return navigateTo(urlCtxPartObj.ORG.path)
      }
    }

    if (targetOrgId && targetOrgId !== ctxStore.active_org_id) {
      ctxStore.active_org_id = targetOrgId
    }
    if (targetCompId && targetCompId !== ctxStore.active_comp_id) {
      ctxStore.active_comp_id = targetCompId
    }

    if (ctxStore.active_comp_id && !ctxStore.ref_comp_map[ctxStore.active_comp_id]?.has_data) {
      logger.debug(`Data not present for compId: ${ctxStore.active_comp_id}. Triggering load.`)
      ctxStore.changeCompById(ctxStore.active_comp_id)
    }

    if (Object.keys(ctxStore.ref_org_map).length === 0) {
      if (to.fullPath !== urlCtxPartObj.CREATE_ACCOUNT.path) {
        logger.warn('🔶 No organizations found. Redirecting to create new org page.')
        return navigateTo(urlCtxPartObj.CREATE_ACCOUNT.path)
      }
    }
  } else if (!isAuthPage) {
    logger.warn('🔴 User is not authenticated, session status: ', $clerk.session?.status)
  }

  logger.log('🔵 Route allowed: ', to.fullPath)
})
